import type {
    Form,
    FormButton,
    FormField,
    FormFieldConditions,
    FormSection,
    FormUploadButton,
} from '../../interfaces/form.interface';
import { FormType } from '../../interfaces/form.interface';
import type { Input } from '../../interfaces/input.interface';
import {
    ReactiveFieldDefinition,
    ReactiveDropdownDefinition,
    ReactiveFormDefinition,
    ReactiveFormMode,
    ReactiveRule,
    ReactiveSectionDefinition,
} from '../../interfaces/reactive-form-definition.interface';
import type { ReactiveFieldDependency, ReactiveDependencyKind } from '../dependencies/reactive-dependency-graph';

const DEFAULT_UPLOAD_BUTTON: FormUploadButton = {
    isEnabled: false,
    label: '',
};

const DEFAULT_FIELD_CONDITIONS: FormFieldConditions = {
    isRememberedOnDuplicate: false,
    isRequired: false,
    isRetained: false,
    hasUndoEnabled: false,
    isVisible: true,
};

const FORM_TYPE_TO_MODE = new Map<FormType, ReactiveFormMode>([
    [FormType.CREATION, 'creation'],
    [FormType.EDITION, 'edition'],
    [FormType.DUPLICATION, 'duplication'],
    [FormType.DELETION, 'deletion'],
    [FormType.INFORMATIVE, 'informative'],
    [FormType.EXPORT, 'export'],
    [FormType.GENERATION, 'generation'],
]);

const MODE_TO_FORM_TYPE = new Map<ReactiveFormMode, FormType>([
    ['creation', FormType.CREATION],
    ['edition', FormType.EDITION],
    ['duplication', FormType.DUPLICATION],
    ['deletion', FormType.DELETION],
    ['informative', FormType.INFORMATIVE],
    ['export', FormType.EXPORT],
    ['generation', FormType.GENERATION],
]);

/**
 * Bridges the legacy `Form<T>` schema and the typed `ReactiveFormDefinition<T>` schema.
 *
 * The adapter is intentionally lossless for legacy definitions: callbacks, render settings,
 * request hooks and other unknown properties are kept in `legacy` metadata and restored when
 * the definition is converted back to the runtime `Form<T>` shape.
 */
export class LegacyFormDefinitionAdapter {
    public static fromLegacy<TModel>(form: Form<TModel>): ReactiveFormDefinition<TModel> {
        return {
            name: form.name,
            title: form.header,
            mode: this.toMode(form.type),
            sections: form.sections.map((section) => this.fromLegacySection(section)),
            table: form.table,
            uploadButton: form.uploadButton,
            ...(form.uploadButtons ? { uploadButtons: [...form.uploadButtons] } : {}),
            ...(form.button ? { button: this.cloneButtonOrButtons(form.button) } : {}),
            ...(form.buttons ? { buttons: [...form.buttons] } : {}),
            signed: form.signed,
            ...(form.signatureConfiguration ? { signatureConfiguration: form.signatureConfiguration } : {}),
            ...(form.serializer ? { serializer: form.serializer } : {}),
            ...(form.dependencies ? { dependencies: [...form.dependencies] } : {}),
            legacy: {
                source: 'legacy-form',
                form,
                ...(form.previousDataTransform ? { previousDataTransform: form.previousDataTransform } : {}),
            },
        };
    }

    public static toLegacy<TModel>(definition: ReactiveFormDefinition<TModel>): Form<TModel> {
        const sourceForm = definition.legacy?.form as Form<TModel> | undefined;
        const uploadButton = definition.uploadButton ?? sourceForm?.uploadButton ?? DEFAULT_UPLOAD_BUTTON;
        const signed = definition.signed ?? sourceForm?.signed ?? false;
        const button = this.resolveButton(definition.button, sourceForm?.button);
        const buttons = this.resolveReadonlyArray(definition.buttons, sourceForm?.buttons);
        const uploadButtons = this.resolveReadonlyArray(definition.uploadButtons, sourceForm?.uploadButtons);

        return {
            ...(sourceForm ?? {}),
            name: definition.name,
            header: definition.title,
            type: this.toFormType(definition.mode),
            sections: definition.sections.map((section) => this.toLegacySection(section)),
            table: definition.table ?? sourceForm?.table ?? null,
            uploadButton,
            ...(uploadButtons ? { uploadButtons } : {}),
            ...(button ? { button } : {}),
            ...(buttons ? { buttons } : {}),
            signed,
            ...(definition.signatureConfiguration ? { signatureConfiguration: definition.signatureConfiguration } : {}),
            ...(definition.serializer ? { serializer: definition.serializer } : {}),
            ...(sourceForm?.previousDataTransform ? { previousDataTransform: sourceForm.previousDataTransform } : {}),
            ...(definition.dependencies ? { dependencies: [...definition.dependencies] } : {}),
        };
    }

    public static toMode(type: FormType): ReactiveFormMode {
        return FORM_TYPE_TO_MODE.get(type) ?? 'creation';
    }

    public static toFormType(mode: ReactiveFormMode): FormType {
        return MODE_TO_FORM_TYPE.get(mode) ?? FormType.CREATION;
    }

    private static fromLegacySection<TModel>(section: FormSection<TModel>): ReactiveSectionDefinition<TModel> {
        return {
            name: section.name,
            width: section.width,
            order: section.order,
            visible: section.isVisible,
            fields: section.fields.map((field) => this.fromLegacyField(field)),
            ...(section.buttons ? { buttons: [...section.buttons] } : {}),
            ...(section.tables ? { tables: [...section.tables] } : {}),
        };
    }

    private static fromLegacyField<TModel>(field: FormField<TModel>): ReactiveFieldDefinition<TModel> {
        const conditions = field.conditions ?? DEFAULT_FIELD_CONDITIONS;

        return {
            name: field.name as keyof TModel & string,
            label: field.label,
            type: field.input.type,
            width: field.width,
            order: field.order,
            input: field.input,
            visible: conditions.isVisible,
            required: conditions.isRequired,
            readOnly: !field.input.isEditable,
            ...(field.computed ? { computed: field.computed } : {}),
            ...(field.input.defaultValue !== undefined ? { defaultValue: field.input.defaultValue } : {}),
            ...(field.dataSource || field.dependencyBehaviour
                ? { dropdown: this.buildDropdownDefinition(field) }
                : {}),
            ...this.withDependencies(this.buildFieldDependencies(field)),
            ...(field.buttons ? { buttons: [...field.buttons] } : {}),
            legacy: {
                field,
                conditions,
                ...(field.vinculatedFields ? { vinculatedFields: [...field.vinculatedFields] } : {}),
                ...(field.turnVisible ? { turnVisible: field.turnVisible } : {}),
                ...(field.updateValue ? { updateValue: field.updateValue } : {}),
                ...(field.checkForDuplicates ? { checkForDuplicates: field.checkForDuplicates } : {}),
            },
        };
    }

    private static toLegacySection<TModel>(section: ReactiveSectionDefinition<TModel>): FormSection<TModel> {
        return {
            name: section.name,
            fields: section.fields.map((field) => this.toLegacyField(field)),
            ...(section.buttons ? { buttons: [...section.buttons] } : {}),
            ...(section.tables ? { tables: [...section.tables] } : {}),
            width: section.width,
            isVisible: section.visible,
            order: section.order,
        };
    }

    private static toLegacyField<TModel>(field: ReactiveFieldDefinition<TModel>): FormField<TModel> {
        const sourceField = field.legacy?.field as FormField<TModel> | undefined;
        const conditions = this.buildLegacyConditions(field, sourceField?.conditions);
        const input = this.buildLegacyInput(field, sourceField?.input);
        const dependencies = this.resolveReadonlyArray(field.dependencies, sourceField?.dependencies);
        const buttons = this.resolveReadonlyArray(field.buttons, sourceField?.buttons);
        const computed = field.computed ?? sourceField?.computed;

        return {
            ...(sourceField ?? {}),
            name: field.name,
            label: field.label,
            input,
            ...(field.dropdown?.dataSource ? { dataSource: field.dropdown.dataSource } : {}),
            ...(field.dropdown?.dependencyBehaviour ? { dependencyBehaviour: field.dropdown.dependencyBehaviour } : {}),
            ...(computed ? { computed } : {}),
            ...(dependencies ? { dependencies } : {}),
            ...(buttons ? { buttons } : {}),
            width: field.width,
            conditions,
            order: field.order,
            vinculatedFields: sourceField?.vinculatedFields,
        };
    }


    private static buildFieldDependencies<TModel>(field: FormField<TModel>): ReactiveFieldDependency[] {
        const dependencies: ReactiveFieldDependency[] = [];

        for (const dependency of field.dependencies ?? []) {
            this.addFieldDependency(dependencies, dependency);
        }

        for (const source of field.dataSource?.dependsOn ?? []) {
            this.addFieldDependency(dependencies, {
                source,
                target: field.name,
                kind: 'dropdown',
                clearOnChange: field.dependencyBehaviour?.clearOnParentChange ?? true,
                reloadOnChange: field.dependencyBehaviour?.reloadOnParentChange ?? false,
                filterLocally: field.dependencyBehaviour?.filterLocally ?? false,
                preserveOnHydration: field.dependencyBehaviour?.clearOnHydration === false,
                legacy: true,
            });
        }

        for (const source of this.toDependencyArray(field.computed?.dependsOn)) {
            this.addFieldDependency(dependencies, {
                source,
                target: field.name,
                kind: 'computed',
            });
        }

        for (const source of this.toDependencyArray(field.input?.dynamicTypeConfiguration?.dependsOn)) {
            this.addFieldDependency(dependencies, {
                source,
                target: field.name,
                kind: 'dynamicType',
                legacy: true,
            });
        }

        for (const source of field.vinculatedFields ?? []) {
            this.addFieldDependency(dependencies, {
                source,
                target: field.name,
                kind: 'legacy',
                legacy: true,
            });

            this.addLegacySpecializedDependency(
                dependencies,
                field,
                source,
                'filter',
                Boolean(field.input?.dropdownConfiguration?.filter),
                {
                    clearOnChange: field.dependencyBehaviour?.clearOnParentChange ?? true,
                    preserveOnHydration: field.dependencyBehaviour?.clearOnHydration === false,
                },
            );
            this.addLegacySpecializedDependency(
                dependencies,
                field,
                source,
                'appendData',
                typeof field.input?.dropdownConfiguration?.appendData === 'function',
            );
            this.addLegacySpecializedDependency(dependencies, field, source, 'visibility', Boolean(field.turnVisible));
            this.addLegacySpecializedDependency(dependencies, field, source, 'computed', Boolean(field.updateValue || field.computed));
        }

        return dependencies;
    }

    private static addLegacySpecializedDependency<TModel>(
        dependencies: ReactiveFieldDependency[],
        field: FormField<TModel>,
        source: string,
        kind: ReactiveDependencyKind,
        shouldAdd: boolean,
        extra: Partial<ReactiveFieldDependency> = {},
    ): void {
        if (!shouldAdd) {
            return;
        }

        this.addFieldDependency(dependencies, {
            source,
            target: field.name,
            kind,
            legacy: true,
            ...extra,
        });
    }

    private static withDependencies(dependencies: ReactiveFieldDependency[]): { dependencies?: ReactiveFieldDependency[] } {
        return dependencies.length > 0 ? { dependencies } : {};
    }

    private static toDependencyArray(dependencies: string | readonly string[] | undefined): readonly string[] {
        if (!dependencies) {
            return [];
        }

        return typeof dependencies === 'string' ? [dependencies] : dependencies;
    }

    private static addFieldDependency(dependencies: ReactiveFieldDependency[], dependency: ReactiveFieldDependency): void {
        if (!dependency.source || !dependency.target) {
            return;
        }

        const exists = dependencies.some((item) =>
            item.source === dependency.source &&
            item.target === dependency.target &&
            item.kind === dependency.kind,
        );

        if (!exists) {
            dependencies.push(dependency);
        }
    }

    private static buildDropdownDefinition<TModel>(field: FormField<TModel>): ReactiveDropdownDefinition<TModel> {
        return {
            ...(field.dataSource ? { dataSource: field.dataSource } : {}),
            ...(field.dependencyBehaviour ? { dependencyBehaviour: field.dependencyBehaviour } : {}),
        };
    }

    private static buildLegacyConditions<TModel>(
        field: ReactiveFieldDefinition<TModel>,
        sourceConditions: FormFieldConditions | undefined,
    ): FormFieldConditions {
        return {
            ...(sourceConditions ?? DEFAULT_FIELD_CONDITIONS),
            isRequired: this.resolveStaticRule(field.required, sourceConditions?.isRequired ?? false),
            isVisible: this.resolveStaticRule(field.visibleWhen ?? field.visible, sourceConditions?.isVisible ?? true),
        };
    }

    private static buildLegacyInput<TModel>(
        field: ReactiveFieldDefinition<TModel>,
        sourceInput: Input<TModel> | undefined,
    ): Input<TModel> {
        const readOnly = this.resolveStaticRule(
            field.disabledWhen ?? field.readonlyWhen ?? field.readOnly,
            sourceInput ? !sourceInput.isEditable : false,
        );

        return {
            ...(sourceInput ?? field.input),
            ...field.input,
            type: field.type,
            isEditable: !readOnly,
            ...(field.defaultValue !== undefined ? { defaultValue: field.defaultValue } : {}),
        } as Input<TModel>;
    }

    private static resolveStaticRule<TModel>(rule: ReactiveRule<TModel> | undefined, fallback: boolean): boolean {
        if (typeof rule === 'boolean') {
            return rule;
        }

        return fallback;
    }

    private static resolveButton(
        nextButton: FormButton | readonly FormButton[] | undefined,
        fallbackButton: FormButton | readonly FormButton[] | undefined,
    ): FormButton | FormButton[] | undefined {
        if (nextButton) {
            return Array.isArray(nextButton) ? [...nextButton] : nextButton as FormButton;
        }

        if (fallbackButton) {
            return Array.isArray(fallbackButton) ? [...fallbackButton] : fallbackButton as FormButton;
        }

        return undefined;
    }

    private static resolveReadonlyArray<TValue>(
        nextValues: readonly TValue[] | undefined,
        fallbackValues: readonly TValue[] | undefined,
    ): TValue[] | undefined {
        if (nextValues) {
            return [...nextValues];
        }

        return fallbackValues ? [...fallbackValues] : undefined;
    }

    private static cloneButtonOrButtons(button: FormButton | FormButton[]): FormButton | FormButton[] {
        return Array.isArray(button) ? [...button] : button;
    }
}
